import { useCallback } from 'react';
import dayjs from 'dayjs';

import { useNavigationOperations } from 'entities/navigation';
import { useApplicationOperations, MODALS } from 'entities/application';
import { useFilterFormContext, useModalContext } from '../context';
import { useValidateFilterForm } from './useValidateFilterForm';
import { FUEL_CODE, ROAD_CARD_E100_CODE, ROAD_OBU_CODE, SERVICE_CODE } from 'shared/constants';
import { useOperations as useUserOperation } from 'store/features/user';
import { logAmplitudeEvent } from 'shared/utils/amplitude/amplitude';

export const useSubmitFilterForm = () => {
  const {
    getLocationsHistory,
    getClientFuelEventsHistory,
    getClientParkingEventsHistory,
    getClientTollsEventsHistory,
    resetLocationsHistory,
  } = useNavigationOperations();
  const { autoObuNumber, startDate, endDate, setFilterFormErrors } = useFilterFormContext();
  const { addActiveModal } = useApplicationOperations();
  const validateFilterForm = useValidateFilterForm();
  const { handleHideContent } = useModalContext();
  const { getHistoryUserLocation, resetHistoryUserLocation } = useUserOperation();

  const submitFilterForm = useCallback(() => {
    resetHistoryUserLocation();
    resetLocationsHistory();
    const errors = validateFilterForm();

    if (!Object.keys(errors).length) {
      logAmplitudeEvent('showHistory', {
        extra: {
          ...(autoObuNumber.obuNumber && { obuNumber: autoObuNumber.obuNumber, licensePlate: autoObuNumber.key }),
          ...(!autoObuNumber.obuNumber && { cardNumbers: autoObuNumber.cardNumbers, licensePlate: autoObuNumber.key }),
          startDate: dayjs(startDate).utcOffset(0).format(),
          endDate: dayjs(endDate).utcOffset(0).format(),
        },
      });
      getClientFuelEventsHistory({
        autoNumber: autoObuNumber.obuNumber ? (autoObuNumber.key as string) : null,
        obuNumber: autoObuNumber.obuNumber ?? null,
        cardNumbers: autoObuNumber.cardNumber ? [autoObuNumber.cardNumber] : null,
        startDate: dayjs(startDate).utcOffset(0).format(),
        endDate: dayjs(endDate).utcOffset(0).format(),
        servicesNts: [FUEL_CODE],
      });
      getClientParkingEventsHistory({
        autoNumber: autoObuNumber.obuNumber ? (autoObuNumber.key as string) : null,
        obuNumber: autoObuNumber.obuNumber ?? null,
        cardNumbers: autoObuNumber.cardNumber ? [autoObuNumber.cardNumber] : null,
        startDate: dayjs(startDate).utcOffset(0).format(),
        endDate: dayjs(endDate).utcOffset(0).format(),
        servicesNts: [SERVICE_CODE],
      });
      getClientTollsEventsHistory({
        autoNumber: autoObuNumber.obuNumber ? (autoObuNumber.key as string) : null,
        obuNumber: autoObuNumber.obuNumber ?? null,
        cardNumbers: autoObuNumber.cardNumber ? [autoObuNumber.cardNumber] : null,
        startDate: dayjs(startDate).utcOffset(0).format(),
        endDate: dayjs(endDate).utcOffset(0).format(),
        servicesNts: [ROAD_CARD_E100_CODE, ROAD_OBU_CODE],
      });
      if (autoObuNumber.obuNumber) {
        getLocationsHistory({
          autoNumber: autoObuNumber.key as string,
          obuNumber: autoObuNumber.obuNumber as string,
          cardNumbers: null,
          startDate: dayjs(startDate).utcOffset(0).format(),
          endDate: dayjs(endDate).utcOffset(0).format(),
        });
      } else {
        getHistoryUserLocation({
          cardNumber: autoObuNumber.cardNumber!,
          startDate: dayjs(startDate).utcOffset(0).format(),
          endDate: dayjs(endDate).utcOffset(0).format(),
        });
      }

      addActiveModal(MODALS.navigationHistory);
      handleHideContent();
    }

    setFilterFormErrors(errors);
  }, [
    resetHistoryUserLocation,
    resetLocationsHistory,
    validateFilterForm,
    setFilterFormErrors,
    autoObuNumber.obuNumber,
    autoObuNumber.key,
    autoObuNumber.cardNumbers,
    autoObuNumber.cardNumber,
    startDate,
    endDate,
    getClientFuelEventsHistory,
    getClientParkingEventsHistory,
    getClientTollsEventsHistory,
    addActiveModal,
    handleHideContent,
    getLocationsHistory,
    getHistoryUserLocation,
  ]);

  return submitFilterForm;
};
